import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './LicensePage.css';

const LicensePage = () => {
  const ipProtections = [
    {
      type: 'Copyright',
      icon: '©',
      description: 'Protecting our creative works',
      details: [
        'All software source code',
        'Documentation and technical writing',
        'Design files and schematics',
        'Website content and media',
        'Educational materials'
      ]
    },
    {
      type: 'Trademark',
      icon: '™',
      description: 'Protecting our brand identity',
      details: [
        'Emerging Robotics name and logo',
        'Gorai framework name and branding',
        'Product visual identity',
        'Marketing materials'
      ]
    },
    {
      type: 'Patents',
      icon: '📋',
      description: 'Protecting our innovations',
      details: [
        'Novel hardware designs',
        'Unique system architectures',
        'Innovative control methods',
        'Pending and future applications',
        'Strategic IP portfolio'
      ]
    }
  ];

  return (
    <div className="license-page">
      {/* Background */}
      <div className="license-bg">
        <div className="bg-gradient" />
        <div className="grid-pattern" />
        <div className="glow-orb orb-1" />
        <div className="glow-orb orb-2" />
      </div>

      {/* Hero */}
      <section className="license-hero">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">License & IP</span>
            <h1>Open Philosophy, Protected Innovation</h1>
            <p className="hero-subtitle">
              We believe in transparency and sharing knowledge, while protecting
              the innovations that make our products possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="philosophy-section">
        <div className="section-container">
          <motion.div
            className="philosophy-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Open Philosophy</h2>
            <p>
              At Emerging Robotics, we believe that openness accelerates innovation.
              We share our knowledge, publish our learnings, and contribute to the
              broader robotics community. We want others to learn from our work,
              build upon our ideas, and push the boundaries of what's possible.
            </p>
            <p>
              However, openness doesn't mean giving away the ability to sustain our
              mission. We're building real products and offering real services. Our
              continued ability to innovate depends on being able to commercialize
              our technology responsibly.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Balance Section */}
      <section className="balance-section">
        <div className="section-container">
          <motion.div
            className="balance-grid"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="balance-card open">
              <h3>Open for Learning</h3>
              <p>
                We're committed to being open for non-commercial use. Researchers,
                students, hobbyists, and educators are welcome to learn from our work,
                experiment with our designs, and use our technology for personal and
                educational projects.
              </p>
              <ul>
                <li>Study and learn from our designs</li>
                <li>Build personal projects</li>
                <li>Educational and research use</li>
                <li>Non-commercial experimentation</li>
              </ul>
            </div>
            <div className="balance-card protected">
              <h3>Protected for Commerce</h3>
              <p>
                We are equally committed to pursuing commercialization of our own
                technology. We ask that others not sell our work without permission.
                If you want to use our technology commercially, let's talk about
                licensing arrangements that work for everyone.
              </p>
              <ul>
                <li>Commercial use requires licensing</li>
                <li>Resale requires partnership</li>
                <li>Manufacturing rights negotiable</li>
                <li>Fair terms for legitimate businesses</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* IP Protection Section */}
      <section className="ip-section">
        <div className="section-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Intellectual Property Protection</h2>
            <p className="section-subtitle">
              We're pursuing all three forms of intellectual property protection to ensure
              we can continue to innovate while keeping our work accessible for learning.
            </p>
          </motion.div>

          <div className="ip-grid">
            {ipProtections.map((ip, index) => (
              <motion.div
                key={ip.type}
                className="ip-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
              >
                <span className="ip-icon">{ip.icon}</span>
                <h3>{ip.type}</h3>
                <p className="ip-description">{ip.description}</p>
                <ul className="ip-details">
                  {ip.details.map((detail, i) => (
                    <li key={i}>{detail}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specific Licenses Section */}
      <section className="licenses-section">
        <div className="section-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Our License Structure</h2>
            <p className="section-subtitle">
              Different types of work require different protections. Here's how we license our various outputs.
            </p>
          </motion.div>

          <motion.div
            className="license-table"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="license-row">
              <div className="license-type">Hardware Designs</div>
              <div className="license-name">CERN-OHL-S v2</div>
              <div className="license-desc">Open hardware license with strong copyleft for commercial use</div>
            </div>
            <div className="license-row">
              <div className="license-type">Software</div>
              <div className="license-name">MIT License</div>
              <div className="license-desc">Permissive open source for maximum compatibility</div>
            </div>
            <div className="license-row">
              <div className="license-type">Documentation</div>
              <div className="license-name">CC BY-SA 4.0</div>
              <div className="license-desc">Creative Commons with attribution and share-alike</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="license-cta-section">
        <div className="section-container">
          <motion.div
            className="cta-box"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Commercial Licensing</h2>
            <p>
              Interested in using our technology commercially? We're open to
              licensing discussions. Reach out and let's find an arrangement
              that works for your business.
            </p>
            <div className="cta-buttons">
              <a href="mailto:gherlein@herlein.com" className="cta-primary">
                Contact Us
              </a>
              <Link to="/services" className="cta-secondary">
                Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Back Link */}
      <div className="section-container">
        <motion.div
          className="back-link"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Link to="/">← Back to Home</Link>
        </motion.div>
      </div>
    </div>
  );
};

export default LicensePage;
